// Запросы организационного контура: юрлицо → заказчик → договор → объект → пост, норматив,
// укомплектованность, типы оформления. Ключи кэша и хуки.
//
// Живёт в общем каталоге, а не внутри раздела «Объекты», потому что этот справочник нужен и
// «Людям» (отбор по объекту, мастер постановки, тип оформления), и «Расчёту». Второй набор
// хуков рядом означал бы второй ключ кэша на те же данные: два запроса на один список,
// и один из них устаревает молча.
//
// Правило 11 (поведение во времени задано числами) реализуется здесь, а не в каждом экране:
//   * placeholderData: keepPreviousData — при смене фильтра старые строки остаются на месте,
//     а не сменяются пустотой на время запроса. Человек дёрнул «только действующие» и
//     продолжает видеть список;
//   * скелет рисуется только там, где данных нет ДАЖЕ в кэше прошлой сессии — это решает
//     ScreenState по data === undefined, а кэш переживает перезагрузку (app/query-client.ts);
//   * спиннера поверх уже показанного числа нет нигде.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { fetchTemplates } from '@/shared/api/endpoints/payroll';
import type { PayrollTemplateOut } from '@/shared/api/endpoints/payroll';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import type { ListResponse } from '@/shared/api/types';
import type {
  ContractIn,
  ContractOut,
  ContractsQuery,
  CustomerIn,
  CustomerOut,
  CustomersQuery,
  EmploymentTypeOut,
  LegalEntityIn,
  LegalEntityOut,
  LegalEntityPatch,
  NormOut,
  NormOverrideIn,
  PostIn,
  PostOut,
  ScheduleTypeOut,
  SiteIn,
  SiteOut,
  SitePatch,
  SiteTypeOut,
  SitesQuery,
  StaffingOut,
} from '@/shared/api/endpoints/org';
import {
  listScheduleTypes,
  patchLegalEntity,
  createPost,
  createSite,
  createCustomer,
  createContract,
  getNorm,
  getSite,
  getStaffing,
  listContracts,
  listCustomers,
  listEmploymentTypes,
  createLegalEntity,
  listLegalEntities,
  listPosts,
  listSiteTypes,
  listSites,
  patchSite,
  setNormOverride,
} from '@/shared/api/endpoints/org';
import type { ApiError } from '@/shared/errors/api-error';
import { подтверждение } from '@/shared/ui/toast/подтверждение';

export { newIdempotencyKey };

/**
 * Ключи кэша. Общий корень 'org' даёт одну команду на сброс всего организационного контура:
 * завели объект — устарели и списки, и карточка, и норматив, и укомплектованность.
 */
export const orgKeys = {
  all: ['org'] as const,
  sites: (params: SitesQuery) => ['org', 'sites', params] as const,
  site: (siteId: string) => ['org', 'site', siteId] as const,
  posts: (siteId: string) => ['org', 'posts', siteId] as const,
  norm: (siteId: string, year: number, month: number) =>
    ['org', 'norm', siteId, year, month] as const,
  staffing: (siteId: string) => ['org', 'staffing', siteId] as const,
  customers: (params: CustomersQuery) => ['org', 'customers', params] as const,
  contracts: (params: ContractsQuery) => ['org', 'contracts', params] as const,
  legalEntities: () => ['org', 'legal-entities'] as const,
  siteTypes: () => ['org', 'site-types'] as const,
  payrollTemplates: () => ['org', 'payroll-templates'] as const,
  employmentTypes: () => ['org', 'employment-types'] as const,
};

/** Справочники живут дольше списков: юрлиц пять, типов объектов десяток, они не бегают. */
const DICTIONARY_STALE_MS = 10 * 60 * 1000;

/**
 * Список объектов. `включено` гасит запрос у того, кому справочники не показываются: право на
 * ведомости и право на справочник объектов — разные, и стучаться в сервер, заранее зная, что он
 * откажет, — это лишний круг по сети и лишняя строка в журнале отказов.
 */
export function useSitesQuery(
  params: SitesQuery,
  включено = true,
): UseQueryResult<ListResponse<SiteOut>, ApiError> {
  return useQuery<ListResponse<SiteOut>, ApiError>({
    queryKey: orgKeys.sites(params),
    queryFn: ({ signal }) => listSites(params, signal),
    enabled: включено,
    placeholderData: keepPreviousData,
  });
}

export function useSiteQuery(
  siteId: string,
  /**
   * Ложь — запрос не уходит вовсе. Нужно там, где объект ещё выбирают: хук нельзя вызвать
   * условно, а спрашивать сервер про несуществующий опознаватель значит получать отказ на
   * каждое нажатие и писать его в журнал.
   */
  enabled = true,
): UseQueryResult<SiteOut, ApiError> {
  return useQuery<SiteOut, ApiError>({
    queryKey: orgKeys.site(siteId),
    queryFn: ({ signal }) => getSite(siteId, signal),
    enabled,
  });
}

/**
 * Справочник режимов работы поста. По режиму строится график смен, и задать его надо при
 * заведении поста: без режима «создать график» не создаёт ничего.
 */
export function useScheduleTypesQuery(): UseQueryResult<ListResponse<ScheduleTypeOut>, ApiError> {
  return useQuery<ListResponse<ScheduleTypeOut>, ApiError>({
    queryKey: ['org', 'post-schedule-types'],
    queryFn: ({ signal }) => listScheduleTypes(signal),
    // Справочник из контракта: он меняется вместе с выкладкой, а не в течение смены.
    staleTime: 10 * 60 * 1000,
  });
}

export function usePostsQuery(
  siteId: string | undefined,
): UseQueryResult<ListResponse<PostOut>, ApiError> {
  return useQuery<ListResponse<PostOut>, ApiError>({
    queryKey: orgKeys.posts(siteId ?? ''),
    queryFn: ({ signal }) => listPosts(siteId ?? '', signal),
    enabled: siteId !== undefined,
    placeholderData: keepPreviousData,
  });
}

export function useNormQuery(
  siteId: string,
  year: number,
  month: number,
): UseQueryResult<NormOut, ApiError> {
  return useQuery<NormOut, ApiError>({
    queryKey: orgKeys.norm(siteId, year, month),
    queryFn: ({ signal }) => getNorm(siteId, year, month, signal),
    // Месяц переключают стрелками туда-сюда: прошлый месяц остаётся на экране,
    // пока не приедет следующий.
    placeholderData: keepPreviousData,
  });
}

export function useStaffingQuery(siteId: string): UseQueryResult<StaffingOut, ApiError> {
  return useQuery<StaffingOut, ApiError>({
    queryKey: orgKeys.staffing(siteId),
    queryFn: ({ signal }) => getStaffing(siteId, signal),
  });
}

/** `включено` — по той же причине, что у объектов: без права на справочники запрос не уходит. */
export function useCustomersQuery(
  params: CustomersQuery,
  включено = true,
): UseQueryResult<ListResponse<CustomerOut>, ApiError> {
  return useQuery<ListResponse<CustomerOut>, ApiError>({
    queryKey: orgKeys.customers(params),
    queryFn: ({ signal }) => listCustomers(params, signal),
    enabled: включено,
    placeholderData: keepPreviousData,
  });
}

export function useContractsQuery(
  params: ContractsQuery,
  enabled = true,
): UseQueryResult<ListResponse<ContractOut>, ApiError> {
  return useQuery<ListResponse<ContractOut>, ApiError>({
    queryKey: orgKeys.contracts(params),
    queryFn: ({ signal }) => listContracts(params, signal),
    enabled,
    staleTime: DICTIONARY_STALE_MS,
  });
}

/**
 * Правка реквизитов юрлица.
 *
 * Инвалидируем весь org-кэш: имя юрлица подставляется в карточку объекта, в мастер нового
 * объекта и в шапку ведомости, и оставить там прежнее написание значило бы показать два разных
 * названия одной компании на соседних экранах.
 */
export function useCreateLegalEntityMutation(): UseMutationResult<
  LegalEntityOut,
  ApiError,
  LegalEntityIn
> {
  const client = useQueryClient();
  return useMutation<LegalEntityOut, ApiError, LegalEntityIn>({
    mutationFn: (body) => createLegalEntity(body),
    meta: подтверждение('Юрлицо заведено'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

export function usePatchLegalEntityMutation(): UseMutationResult<
  LegalEntityOut,
  ApiError,
  { id: string; body: LegalEntityPatch }
> {
  const client = useQueryClient();
  return useMutation<LegalEntityOut, ApiError, { id: string; body: LegalEntityPatch }>({
    mutationFn: ({ id, body }) => patchLegalEntity(id, body),
    meta: подтверждение('Юрлицо сохранено'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

/** `включено` — по той же причине, что у объектов: без права на справочники запрос не уходит. */
export function useLegalEntitiesQuery(
  включено = true,
): UseQueryResult<ListResponse<LegalEntityOut>, ApiError> {
  return useQuery<ListResponse<LegalEntityOut>, ApiError>({
    queryKey: orgKeys.legalEntities(),
    queryFn: ({ signal }) => listLegalEntities(signal),
    enabled: включено,
    staleTime: DICTIONARY_STALE_MS,
  });
}

/**
 * Шаблоны ведомости: набор и порядок колонок объекта.
 *
 * Лежит здесь, а не в разделе «Расчёт», по той же причине, что и типы объектов: шаблон
 * НАЗНАЧАЮТ объекту, и карточка объекта обязана уметь назвать его словами. До 25.08.2026 она
 * писала в этом поле «назначен» — то есть отвечала «да» на вопрос «какой».
 */
export function usePayrollTemplatesQuery(): UseQueryResult<
  ListResponse<PayrollTemplateOut>,
  ApiError
> {
  return useQuery<ListResponse<PayrollTemplateOut>, ApiError>({
    queryKey: orgKeys.payrollTemplates(),
    queryFn: ({ signal }) => fetchTemplates({ signal }),
    staleTime: DICTIONARY_STALE_MS,
  });
}

export function useSiteTypesQuery(): UseQueryResult<ListResponse<SiteTypeOut>, ApiError> {
  return useQuery<ListResponse<SiteTypeOut>, ApiError>({
    queryKey: orgKeys.siteTypes(),
    queryFn: ({ signal }) => listSiteTypes(signal),
    staleTime: DICTIONARY_STALE_MS,
  });
}

/** Типы оформления: штат, договор, самозанятый. Ведёт администратор, набор меняется редко. */
export function useEmploymentTypesQuery(): UseQueryResult<
  ListResponse<EmploymentTypeOut>,
  ApiError
> {
  return useQuery<ListResponse<EmploymentTypeOut>, ApiError>({
    queryKey: orgKeys.employmentTypes(),
    queryFn: ({ signal }) => listEmploymentTypes(signal),
    staleTime: DICTIONARY_STALE_MS,
  });
}

// ── Изменения ─────────────────────────────────────────────────────────────────────────────

export function useCreateSiteMutation(
  idempotencyKey: string,
): UseMutationResult<SiteOut, ApiError, SiteIn> {
  const client = useQueryClient();
  return useMutation<SiteOut, ApiError, SiteIn>({
    mutationFn: (body) => createSite(body, idempotencyKey),
    meta: подтверждение('Объект заведён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

/**
 * Заведение заказчика.
 *
 * До 26.08.2026 дверь была, а экрана не было: на чистой системе заказчика нельзя было завести
 * вовсе, а без него не заводится объект — и пустой экран заказчиков честно писал «Заказчик
 * нужен объекту при создании», не предлагая ни одного способа его завести. Та же дыра, что
 * «первого администратора было некому завести», и обнаруживается она первой живой попыткой.
 */
export function useCreateCustomerMutation(
  idempotencyKey: string,
): UseMutationResult<CustomerOut, ApiError, CustomerIn> {
  const client = useQueryClient();
  return useMutation<CustomerOut, ApiError, CustomerIn>({
    mutationFn: (body) => createCustomer(body, idempotencyKey),
    meta: подтверждение('Заказчик заведён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

export function useCreateContractMutation(
  idempotencyKey: string,
): UseMutationResult<ContractOut, ApiError, ContractIn> {
  const client = useQueryClient();
  return useMutation<ContractOut, ApiError, ContractIn>({
    mutationFn: (body) => createContract(body, idempotencyKey),
    meta: подтверждение('Договор заведён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

export function useCreatePostMutation(
  siteId: string,
  idempotencyKey: string,
): UseMutationResult<PostOut, ApiError, PostIn> {
  const client = useQueryClient();
  return useMutation<PostOut, ApiError, PostIn>({
    mutationFn: (body) => createPost(siteId, body, idempotencyKey),
    meta: подтверждение('Пост заведён'),
    onSuccess: () => {
      // Пост меняет и список постов, и расчётный норматив, и укомплектованность объекта.
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

export function usePatchSiteMutation(
  siteId: string,
): UseMutationResult<SiteOut, ApiError, SitePatch> {
  const client = useQueryClient();
  return useMutation<SiteOut, ApiError, SitePatch>({
    mutationFn: (body) => patchSite(siteId, body),
    meta: подтверждение('Объект сохранён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

export interface NormOverrideVariables {
  year: number;
  month: number;
  body: NormOverrideIn;
}

export function useNormOverrideMutation(
  siteId: string,
): UseMutationResult<NormOut, ApiError, NormOverrideVariables> {
  const client = useQueryClient();
  return useMutation<NormOut, ApiError, NormOverrideVariables>({
    mutationFn: ({ year, month, body }) => setNormOverride(siteId, year, month, body),
    meta: подтверждение('Норматив на месяц изменён'),
    onSuccess: (data) => {
      client.setQueryData(orgKeys.norm(siteId, data.year, data.month), data);
      void client.invalidateQueries({ queryKey: ['org', 'norm', siteId] });
    },
  });
}
